import React, { useEffect, useMemo, useState } from 'react'
import { useFeatureFlags } from '@fs/zion-flags'
import { Paragraph, Separator } from '@fs/zion-ui'
import ZionForm, { useFormState, useZionForm } from '@fs/zion-form'
import cloneDeep from 'lodash.clonedeep'
import {
  generateSls,
  getImage,
  getNestedOrFloatingElementById,
  getNestedSubelementsByType,
  getSlsTypeByValue,
  saveSlsAndUpdateState,
  useSlsDispatchContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { useTranslation } from 'react-i18next'
import {
  getPrefixedFormDefaultValues,
  getPersonsFromElement,
  getSelectedAndDisplayPersonsByArk,
} from './utils/selectPersonsUtils'
import { errorCollector } from '../../../../../shared/utils/utils'
import ErrorOverlay from '../../../../../shared/components/errorOverlay/ErrorOverlay'
import { headers } from '../../../../../shared/services/config'
import EventPersonSelectionGroups from './EventPersonSelectionGroups'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'

/**
 * Callback function for the onSave event data.
 *
 * @callback onSaveCallback
 * @param {Object[]} dispatches - An array of dispatches.
 * @param {Object[]} formValues - The form values object from the event panel.
 */

/**
 * Callback function for the View In Context button.
 *
 * @callback viewInContextHandler
 * @param {Number} pageNumber - image index + 1
 */

/**
 * Component for showing the attachments of an event
 * @param {Object} selectedPerson - the selected person, who is already attached to this event
 * @param {Object} event - the event to display attachments for
 * @param {Boolean} shouldSaveToSls - whether to save the changes to SLS or just pass the dispatches to onSave
 * @param {Function} onSave - Called with dispatches either after or instead of save
 * @param {Function} viewInContextHandler - Called with pageNumber. Should display the corresponding image.
 * @param {String} addedEventId - if this event is not yet created, this is the id it will have
 * @param {Object} defaultFormState - Optional object to override the default form state. The parent component can preserve previous changes by passing this.
 * @param {String} appName - name of the app that is using this component
 * @param {Function} setSaving - setter for a state variable that tracks if a save request is running
 * @param {Function} onDirtyChange - Optional callback for when form dirty state changes.
 * @returns {Element}
 */
export default function SharedEventContent({
  selectedPerson,
  event,
  shouldSaveToSls = false,
  onSave = () => {},
  viewInContextHandler,
  addedEventId,
  defaultFormState,
  appName,
  setSaving,
  onDirtyChange = () => {},
}) {
  if (!appName) throw new Error('SharedEventPanel: appName is required parameter')

  const [t] = useTranslation()
  const zionSlsState = useSlsStateContext()
  const zionSlsDispatch = useSlsDispatchContext()
  const [error, setError] = useState()
  const { closeOverlay } = useOverlayContext()
  const {
    rip_sharedImageViewer_saveChanges: saveChangesFlag,
    rip_sharedImageViewer_sls_actions_splunk_debugger: slsActionsSplunkDebugger,
  } = useFeatureFlags(['rip_sharedImageViewer_saveChanges', 'rip_sharedImageViewer_sls_actions_splunk_debugger'])

  const eventType = event?.eventType ? getSlsTypeByValue(event.eventType, zionSlsState.types) : null
  const eventTypeLabel = eventType?.displayLabel
  const currentPersonRecord = getNestedOrFloatingElementById(zionSlsState.images, selectedPerson?.recordId)
  const personsInThisRecord = useMemo(
    () => currentPersonRecord?.subElements?.filter((se) => se.elementType === 'PERSON') || [],
    [currentPersonRecord?.subElements]
  )
  const currentImage = getImage(zionSlsState.images, zionSlsState.currentImageArk)
  const personsInThisImage = useMemo(() => {
    return getNestedSubelementsByType(currentImage, 'PERSON').filter(
      (p) => p.partitionKey === zionSlsState.currentImageArk
    )
  }, [zionSlsState.currentImageArk, currentImage])
  const personsFromElement = useMemo(
    () =>
      getPersonsFromElement({
        element: event,
        images: zionSlsState.images,
        selectedPersonId: selectedPerson.id,
      }),
    [event, selectedPerson.id, zionSlsState.images]
  )

  const allTheArks = useMemo(
    () =>
      getSelectedAndDisplayPersonsByArk({
        allPersonsSharingElement: personsFromElement,
        personsInThisRecord,
      }),
    [personsFromElement, personsInThisRecord]
  )

  const allTheArksExceptCurrent = useMemo(() => {
    if (allTheArks) {
      const clone = cloneDeep(allTheArks)
      delete clone[zionSlsState.currentImageArk]
      return clone
    }
    return {}
  }, [allTheArks, zionSlsState.currentImageArk])

  const personsSharingElementOnImage = useMemo(
    () => allTheArks?.[zionSlsState.currentImageArk]?.selected || [],
    [allTheArks, zionSlsState.currentImageArk]
  )

  const allPersonIdsSharingElement = useMemo(
    () => personsSharingElementOnImage.map((p) => p.id),
    [personsSharingElementOnImage]
  )

  const formDefaultValues = useMemo(
    () =>
      defaultFormState ||
      getPrefixedFormDefaultValues({
        personIdsSharingElement: allPersonIdsSharingElement,
        allTheArksExceptCurrent,
        personsInThisRecord,
        personsInThisImage,
      }),
    [allPersonIdsSharingElement, personsInThisRecord, personsInThisImage, allTheArksExceptCurrent, defaultFormState]
  )

  const handleSubmit = (values) => {
    const mergedValues = { ...values.selectedInImage, ...values.selectedInRecord, ...values.selectedInOtherArks }
    const mergedDefaultValues = {
      ...formDefaultValues.selectedInImage,
      ...formDefaultValues.selectedInRecord,
      ...formDefaultValues.selectedInOtherArks,
    }
    const dispatches = []

    const personIds = Object.keys(mergedValues)
    personIds.forEach((personId) => {
      if (mergedValues[personId] !== mergedDefaultValues[personId]) {
        const editingPerson = getNestedOrFloatingElementById(zionSlsState.images, personId)
        if (mergedValues[personId]) {
          // Link the person to the event
          dispatches.push(
            {
              type: 'ADD_SUB',
              value: {
                superPartitionKey: editingPerson.partitionKey,
                subPartitionKey: event ? event.partitionKey : selectedPerson.partitionKey,
                superId: personId,
                subId: event?.id || addedEventId,
                elementType: 'PERSON',
              },
            },
            {
              type: 'SET_RECORD_PERSON_IDS',
              value: editingPerson.recordId,
            }
          )
        } else {
          // Remove the person from the event
          dispatches.push({
            type: 'REM_SUB',
            value: {
              superPartitionKey: editingPerson.partitionKey,
              subPartitionKey: event?.partitionKey,
              superId: personId,
              subId: event?.id,
              elementType: 'PERSON',
            },
          })
        }
      }
    })

    if (shouldSaveToSls) {
      try {
        const sls = generateSls(dispatches)
        setSaving(true)
        saveSlsAndUpdateState({
          sls,
          dispatches,
          saveChangesFlag,
          slsActionsSplunkDebugger,
          dispatch: zionSlsDispatch,
          appName,
          fsUserAgentChain: headers['FS-User-Agent-Chain'],
          successHandler: () => {
            setSaving(false)
            closeOverlay('sharedEvent')
            onSave(dispatches, values)
          },
          errorHandler: (err) => {
            setSaving(false)
            setError(err)
            errorCollector(err, null, {
              codeId: 80,
              sls,
              status: err?.response?.status,
              responseBody: err?.response?.data,
            })
          },
          saveInitiatorFilename: 'SharedEventContent.js (Generic)',
        })
      } catch (err) {
        setSaving(false)
        setError(err)
        errorCollector(err, null, {
          codeId: 80,
          sls: err?.sls,
          dispatches: err?.dispatches,
        })
      }
    } else {
      onSave(dispatches, values)
      closeOverlay('sharedEvent')
    }
  }

  const methods = useZionForm({
    mode: 'all',
    defaultValues: formDefaultValues,
  })

  // Track form dirty state to inform parent component so it can trigger unsaved changes overlay
  const { isDirty } = useFormState({ control: methods.control })

  // Notify parent of dirty state changes
  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  return (
    <>
      <Paragraph size="sm">
        {t('shared-event.panel-description', 'Do more names need to be linked to this {eventType} event?', {
          eventType: eventTypeLabel,
        })}
      </Paragraph>
      <Separator size="sm" />
      <ZionForm methods={methods} onSubmit={handleSubmit} id="shared-event">
        <EventPersonSelectionGroups
          personsInThisRecord={personsInThisRecord}
          personsInThisImage={personsInThisImage}
          allTheArksExceptCurrent={allTheArksExceptCurrent}
          viewInContextHandler={viewInContextHandler}
        />
      </ZionForm>
      <ErrorOverlay
        error={error}
        setError={setError}
        title={t('save-error-overlay.heading', 'Unable to Save Changes')}
        message={t('save-error-overlay.content-generic', 'Your changes were not saved.  Please try again later.')}
      />
    </>
  )
}
